import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  COST_CATEGORIES,
  COST_CATEGORY_LABELS,
  type CostCategory,
} from '@camper-diary/shared';
import {
  createCost,
  deleteCost,
  listCosts,
  updateCost,
  type Cost,
} from '../api/costs';
import { HttpError } from '../api/client';

type Mode = { kind: 'idle' } | { kind: 'creating' } | { kind: 'editing'; cost: Cost };

export function CostsSection({ tripId }: { tripId: string }) {
  const [costs, setCosts] = useState<Cost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setCosts(await listCosts(tripId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento spese');
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  async function onDelete(c: Cost) {
    if (!confirm('Eliminare questa voce di spesa?')) return;
    setBusy(true);
    try {
      await deleteCost(tripId, c.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore eliminazione');
    } finally {
      setBusy(false);
    }
  }

  // Totali per (valuta × categoria)
  const stats = useMemo(() => {
    if (!costs)
      return { byCurrency: new Map<string, number>(), byCategory: new Map<string, Map<CostCategory, number>>() };
    const byCurrency = new Map<string, number>();
    const byCategory = new Map<string, Map<CostCategory, number>>();
    for (const c of costs) {
      byCurrency.set(c.currency, (byCurrency.get(c.currency) ?? 0) + c.amount);
      let inner = byCategory.get(c.currency);
      if (!inner) {
        inner = new Map();
        byCategory.set(c.currency, inner);
      }
      inner.set(c.category, (inner.get(c.category) ?? 0) + c.amount);
    }
    return { byCurrency, byCategory };
  }, [costs]);

  return (
    <section className="costs-section">
      <div className="section-head">
        <h2>Costi</h2>
        {mode.kind === 'idle' && (
          <button
            className="btn btn-primary"
            onClick={() => setMode({ kind: 'creating' })}
            disabled={busy}
          >
            + Nuova spesa
          </button>
        )}
      </div>

      {error && <p className="err">{error}</p>}

      {/* Totali */}
      {costs && costs.length > 0 && (
        <div className="cost-totals">
          {Array.from(stats.byCurrency.entries()).map(([cur, total]) => {
            const inner = stats.byCategory.get(cur);
            return (
              <div key={cur} className="cost-total-card">
                <div className="cost-total-head">
                  <span className="muted">Totale {cur}</span>
                  <span className="total-amount">{formatMoney(total, cur)}</span>
                </div>
                <ul className="cost-by-cat">
                  {COST_CATEGORIES.map((cat) => {
                    const v = inner?.get(cat) ?? 0;
                    if (v === 0) return null;
                    const meta = COST_CATEGORY_LABELS[cat];
                    const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                    return (
                      <li key={cat}>
                        <span>
                          {meta.icon} {meta.label}
                        </span>
                        <span className="cat-amount">
                          {formatMoney(v, cur)} <span className="muted">· {pct}%</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {mode.kind === 'creating' && (
        <CostForm
          onCancel={() => setMode({ kind: 'idle' })}
          onSubmit={async (data) => {
            await createCost(tripId, data);
            setMode({ kind: 'idle' });
            await refresh();
          }}
        />
      )}

      {mode.kind === 'editing' && (
        <CostForm
          initial={mode.cost}
          onCancel={() => setMode({ kind: 'idle' })}
          onSubmit={async (data) => {
            await updateCost(tripId, mode.cost.id, data);
            setMode({ kind: 'idle' });
            await refresh();
          }}
        />
      )}

      {!costs && !error && <p className="muted">Caricamento…</p>}

      {costs && costs.length === 0 && mode.kind === 'idle' && (
        <p className="muted">Nessuna spesa registrata.</p>
      )}

      {costs && costs.length > 0 && (
        <ul className="cost-list">
          {[...costs]
            .sort((a, b) => b.date.localeCompare(a.date))
            .map((c) => {
              const meta = COST_CATEGORY_LABELS[c.category];
              return (
                <li key={c.id} className="cost-item">
                  <span className="cost-icon" aria-hidden>
                    {meta.icon}
                  </span>
                  <div className="cost-main">
                    <div className="cost-line">
                      <strong>{meta.label}</strong>
                      <span className="cost-amount">{formatMoney(c.amount, c.currency)}</span>
                    </div>
                    <div className="cost-meta">
                      <span className="muted">{formatDate(c.date)}</span>
                      {c.description && <span> · {c.description}</span>}
                    </div>
                  </div>
                  <div className="cost-actions">
                    <button
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => setMode({ kind: 'editing', cost: c })}
                    >
                      Modifica
                    </button>
                    <button
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() => void onDelete(c)}
                    >
                      Elimina
                    </button>
                  </div>
                </li>
              );
            })}
        </ul>
      )}
    </section>
  );
}

type FormData = {
  category: CostCategory;
  amount: number;
  currency: string;
  date: string;
  description: string | null;
};

function CostForm({
  initial,
  onCancel,
  onSubmit,
}: {
  initial?: Cost;
  onCancel: () => void;
  onSubmit: (data: FormData) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [category, setCategory] = useState<CostCategory>(initial?.category ?? 'CARBURANTE');
  const [amount, setAmount] = useState<string>(initial?.amount?.toString() ?? '');
  const [currency, setCurrency] = useState<string>(initial?.currency ?? 'EUR');
  const [date, setDate] = useState<string>(initial?.date ?? today);
  const [description, setDescription] = useState<string>(initial?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const a = Number(amount.replace(',', '.'));
    if (!Number.isFinite(a) || a < 0) {
      setError('Importo non valido');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        category,
        amount: a,
        currency: currency.toUpperCase(),
        date,
        description: description.trim() || null,
      });
    } catch (err) {
      setError(err instanceof HttpError ? err.apiError.message : 'Errore di connessione');
      setSubmitting(false);
    }
  }

  return (
    <form className="form-card cost-form" onSubmit={handleSubmit}>
      <div className="row">
        <label>
          <span>Categoria *</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as CostCategory)}
            required
          >
            {COST_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {COST_CATEGORY_LABELS[c].icon} {COST_CATEGORY_LABELS[c].label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Data *</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
      </div>

      <div className="row">
        <label>
          <span>Importo *</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            inputMode="decimal"
            autoFocus
          />
        </label>
        <label>
          <span>Valuta</span>
          <input
            type="text"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
            maxLength={3}
            pattern="[A-Z]{3}"
            placeholder="EUR"
          />
        </label>
      </div>

      <label>
        <span>Descrizione</span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          placeholder="es. pieno autostrada Brescia"
        />
      </label>

      {error && <p className="err">{error}</p>}

      <div className="actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
          Annulla
        </button>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Salvataggio…' : initial ? 'Salva modifiche' : 'Aggiungi'}
        </button>
      </div>
    </form>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function formatMoney(n: number, cur: string): string {
  try {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${cur}`;
  }
}
